import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";
import { type AxiosResponse } from "axios";

import { queryClient } from "@/api/core/client";
import { useApi } from "@/hooks";

import { getEntityKey } from "../utils";

import type { Entity, EntitySchema, Params } from "../types";

type UseEntityQueryOptions<TEntity extends Entity> = Omit<
  UseQueryOptions<TEntity, unknown, TEntity, QueryKey>,
  "queryKey" | "queryFn" | "initialData" | "placeholderData" | "meta"
>;

interface UseEntityOptions<
  TEntity extends Entity,
> extends UseEntityQueryOptions<TEntity> {
  id: string | null;
  idAttributeFromVariables?: string;
  useInitialDataAsPlaceholder?: boolean;
  queryKey: QueryKey;
  schema: EntitySchema<TEntity>;
  endpoint: string;
  params?: Params;
  getResponseHeaders?(headers: AxiosResponse["headers"]): void;
}

function useEntity<TEntity extends Entity>(options: UseEntityOptions<TEntity>) {
  const api = useApi();

  const {
    endpoint,
    schema,
    id,
    idAttributeFromVariables,
    params = {},
    useInitialDataAsPlaceholder = false,
    getResponseHeaders,
    ...filteredOptions
  } = options;

  const entity = getEntityKey(filteredOptions.queryKey);

  const getInitialData = () => {
    const { queryKey } = filteredOptions;
    const globalQueryKey = [queryKey[0]];

    // Fetch an entity from the cache
    const queryCache = queryClient.getQueryData(globalQueryKey) as
      | TEntity[]
      | undefined;
    if (queryCache) {
      return queryCache.find((entity) => {
        if (idAttributeFromVariables) {
          return (entity as any)[idAttributeFromVariables] === id;
        }

        return entity.id === id;
      });
    }
  };

  const queryInfo = useQuery({
    ...filteredOptions,
    meta: { entity },
    queryFn: async ({ signal }) => {
      const response = await api.get<TEntity | TEntity[]>(endpoint, {
        params,
        signal,
      });
      const { data } = response;
      // Check if data is Array (endpoints like fetching a relationship, which returns an array)
      const parsedResult = schema.safeParse(
        Array.isArray(data) ? data[0] : data,
      );

      if (getResponseHeaders) {
        getResponseHeaders(response.headers);
      }

      if (parsedResult.success) {
        return parsedResult.data;
      }

      return {} as TEntity;
    },
    initialData() {
      if (!useInitialDataAsPlaceholder) {
        return getInitialData();
      }
    },
    placeholderData() {
      if (useInitialDataAsPlaceholder) {
        return getInitialData();
      }
    },
  });

  return {
    ...queryInfo,
    entity: queryInfo.data,
  };
}

export { useEntity };
